/**
 * Auth Provider — Keycloak OIDC Integration (D-029)
 *
 * Wraps the app with `react-oidc-context`'s AuthProvider, which manages the
 * full OIDC lifecycle: login redirect, token exchange, silent renewal, logout.
 *
 * ─── Architecture ──────────────────────────────────────────────────────────
 * This file bridges Keycloak's OIDC tokens into React context that the rest
 * of the app consumes via useAuth(). It maps the library's generic OIDC user
 * object to our domain-specific AuthUser interface.
 *
 * ─── JS Parallel ───────────────────────────────────────────────────────────
 * If you've used `useSession()` from NextAuth.js or Firebase's `onAuthStateChanged`,
 * this is the same pattern: a provider at the root that exposes auth state to
 * all descendants via a React context hook. The difference is the token source
 * (Keycloak OIDC vs Firebase proprietary).
 *
 * ─── Token Flow ────────────────────────────────────────────────────────────
 *   1. User clicks "Login" → signinRedirect() → browser navigates to Keycloak
 *   2. User authenticates → Keycloak redirects back with ?code=...
 *   3. react-oidc-context exchanges code for tokens (via PKCE)
 *   4. Tokens stored in sessionStorage (see auth-config.ts)
 *   5. useAuth().token returns the access_token for API calls
 *   6. automaticSilentRenew refreshes token before expiry (hidden iframe)
 */

import {
  AuthProvider as OidcAuthProvider,
  useAuth as useOidcAuth,
} from "react-oidc-context";
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { oidcConfig } from "../lib/auth-config";
import { setAuthToken, setTokenRefresher } from "../services/api";

// ─── Domain Types ─────────────────────────────────────────────────────────
// These types define what the rest of the app sees — decoupled from the
// OIDC library's internal types. If we swap identity providers, only this
// file changes.

/** User profile extracted from Keycloak JWT claims. */
interface AuthUser {
  id: string;
  username: string;
  email: string;
  name: string;
  roles: string[];
}

/** Auth state + actions exposed to the app via useAuth(). */
interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** OIDC error (token exchange failure, PKCE mismatch, user denied consent). */
  error: Error | null;
  /**
   * Initiate OIDC login. Optionally pass an IdP hint to skip the Keycloak
   * login form and redirect directly to that identity provider.
   *
   * JS Parallel: Like Firebase's signInWithPopup(googleProvider) vs
   * signInWithEmailAndPassword() — the hint selects the auth method.
   *
   * @param idpHint - Keycloak IdP alias (e.g., "google"). Maps to the
   *   kc_idp_hint query parameter in the OIDC authorization request.
   */
  login: (idpHint?: string) => void;
  logout: () => void;
}

// null default — useAuth() throws if called outside AuthProvider,
// matching the same pattern as useDataMode(). This prevents silent
// bugs where components outside the provider tree render a permanent
// spinner (isLoading: true) with no error.
const AuthContext = createContext<AuthContextValue | null>(null);

// ─── Keycloak Token Claims ─────────────────────────────────────────────────
// Keycloak's access token contains these claims (standard OIDC + Keycloak-specific).
// The `realm_access.roles` claim is Keycloak-specific — other providers use
// different claim names for roles (e.g., Auth0 uses custom namespaces).

/** Shape of Keycloak-specific claims in the JWT access token. */
interface KeycloakTokenClaims {
  sub: string;
  preferred_username?: string;
  email?: string;
  name?: string;
  given_name?: string;
  family_name?: string;
  // Keycloak-specific: realm-level roles assigned to the user.
  // This nested structure differs from standard OIDC — it's a Keycloak convention.
  realm_access?: {
    roles?: string[];
  };
}

/**
 * Maps Keycloak OIDC user profile to our domain AuthUser.
 *
 * The OIDC `User` object contains the full token payload. We extract only
 * the claims we need and normalize them into our interface. This keeps the
 * rest of the app unaware of Keycloak-specific claim names.
 */
function mapKeycloakUser(profile: KeycloakTokenClaims): AuthUser {
  return {
    id: profile.sub,
    username: profile.preferred_username ?? "unknown",
    email: profile.email ?? "",
    name:
      profile.name ??
      profile.given_name ??
      profile.preferred_username ??
      "User",
    // Filter out Keycloak's internal roles (prefixed with "default-roles-" or
    // framework roles like "offline_access", "uma_authorization").
    // We only want our custom realm roles: "user", "admin".
    roles: (profile.realm_access?.roles ?? []).filter(
      (r) =>
        !r.startsWith("default-roles-") &&
        r !== "offline_access" &&
        r !== "uma_authorization",
    ),
  };
}

/**
 * Inner provider that consumes react-oidc-context and maps it to our
 * AuthContext. Separated from the outer OidcAuthProvider because useOidcAuth()
 * can only be called inside that provider's tree.
 */
function AuthBridge({ children }: { children: ReactNode }) {
  const {
    user: oidcUser,
    isLoading,
    error: oidcError,
    signinRedirect,
    signinSilent,
    signoutRedirect,
  } = useOidcAuth();

  // Sync the access token to the API client whenever it changes.
  // When the OIDC library gets/refreshes a token, we push it to the api.ts
  // module so all subsequent fetch() calls include the Authorization header.
  const accessToken = oidcUser?.access_token ?? null;

  // Set token synchronously during render so it's available before children's
  // effects fire. React's effect ordering is depth-first (children → parents),
  // so without this, a child's useQuery fires its fetch BEFORE this parent's
  // useEffect sets the token — causing a 401 race condition.
  //
  // RENDER-TIME SIDE EFFECT: intentional. setAuthToken only assigns a
  // module-level variable (no DOM mutation, no React state update, no
  // side effect that React's concurrent mode would care about). Same
  // category as writing to a memoization cache or a logger.
  // NOTE: This render-time call is intentionally NOT moved to useEffect.
  // It must run BEFORE children's effects fire to prevent 401 race
  // conditions. Do not "clean up" into useEffect — see comment above.
  setAuthToken(accessToken);

  // Keep the useEffect for post-commit synchronization.
  // React StrictMode may re-invoke both renders and effects in development.
  // No cleanup on unmount: AuthBridge only unmounts when the whole app
  // tears down, so clearing the token is unnecessary and would cause a
  // brief null-token window during StrictMode's unmount/remount cycle.
  useEffect(() => {
    setAuthToken(accessToken);
  }, [accessToken]);

  // Register the token refresher for 401 retry (TF.5.3, issue #91).
  // This gives the plain-module API client a way to trigger signinSilent()
  // when it receives a 401 — bridging React's OIDC context into the
  // framework-agnostic fetch wrapper.
  //
  // The refresher is a stable closure over signinSilent — we re-register
  // when signinSilent changes (which in practice is only on mount, since
  // react-oidc-context memoizes it).
  useEffect(() => {
    setTokenRefresher(async () => {
      try {
        const user = await signinSilent();
        const newToken = user?.access_token ?? null;
        if (newToken) {
          setAuthToken(newToken);
        }
        return newToken;
      } catch {
        // Silent refresh failed — session expired or Keycloak unreachable.
        // Return null so the API client throws the original 401, which
        // the UI can handle by redirecting to login.
        return null;
      }
    });

    return () => setTokenRefresher(null);
  }, [signinSilent]);

  // Memoize the context value to prevent unnecessary re-renders in consumers.
  // Without this, every OIDC state change (even unrelated ones like `isLoading`
  // toggling) would create a new object reference, triggering re-renders in
  // every component that calls useAuth().
  const value = useMemo<AuthContextValue>(() => {
    const profile = oidcUser?.profile as KeycloakTokenClaims | undefined;

    return {
      user: profile ? mapKeycloakUser(profile) : null,
      token: accessToken,
      // A user is authenticated if the OIDC library has a user object AND
      // the token hasn't expired. `user.expired` is a computed property from
      // oidc-client-ts that checks `exp` claim vs current time.
      isAuthenticated: !!oidcUser && !oidcUser.expired,
      isLoading,
      // Surface OIDC errors (expired code, PKCE mismatch, user denied consent)
      // so CallbackPage and other consumers can display specific error messages
      // instead of silently swallowing failures.
      error:
        oidcError instanceof Error
          ? oidcError
          : oidcError
            ? new Error(String(oidcError))
            : null,
      // kc_idp_hint is a Keycloak-specific query parameter that tells
      // Keycloak to skip its login form and redirect directly to the
      // specified identity provider (e.g., "google"). Without it,
      // Keycloak shows its own login page with all configured IdPs.
      login: (idpHint?: string) =>
        void signinRedirect(
          idpHint ? { extraQueryParams: { kc_idp_hint: idpHint } } : undefined,
        ),
      logout: () =>
        void signoutRedirect({
          // id_token_hint tells Keycloak which session to end.
          // Without this, Keycloak shows a "which account?" confirmation page.
          id_token_hint: oidcUser?.id_token,
        }),
    };
  }, [
    oidcUser,
    isLoading,
    oidcError,
    accessToken,
    signinRedirect,
    signoutRedirect,
  ]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ─── Demo-Only Mode (backend decommissioned) ──────────────────────────────
// The Crucio backend (Cloud Run API, Keycloak) has been fully decommissioned.
// The site is now a STATIC DEMO. We must NEVER mount OidcAuthProvider, since
// that would attempt Keycloak network calls (silent renew / signin redirect)
// against a dead identity server. Instead we supply a static, unauthenticated
// context with no-op login/logout so no sign-in flow can reach a dead backend.
//
// Typed as `boolean` (not the literal `true`) so the OIDC branch below stays
// reachable for the type-checker — the original Keycloak wiring is preserved,
// just gated off. Flip to `false` to restore live auth if a backend returns.
const DEMO_ONLY: boolean = true;

/**
 * Static auth context for demo-only mode: nobody is signed in, and
 * login/logout are no-ops so no dead-backend redirect can ever fire.
 * Module-level constant → stable reference (no needless consumer re-renders).
 */
const DEMO_AUTH_VALUE: AuthContextValue = {
  user: null,
  token: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  login: () => {},
  logout: () => {},
};

/**
 * Root auth provider — wraps OidcAuthProvider (library) → AuthBridge (mapping).
 *
 * Place this near the top of the component tree, above any component that
 * needs auth state. The OIDC library handles:
 *   - Initial token load from sessionStorage on app start
 *   - Silent token renewal before expiry
 *   - Redirect handling for login/logout flows
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  // Demo-only: bypass Keycloak/OIDC entirely — no auth network calls on mount.
  if (DEMO_ONLY) {
    return (
      <AuthContext.Provider value={DEMO_AUTH_VALUE}>
        {children}
      </AuthContext.Provider>
    );
  }

  return (
    <OidcAuthProvider
      {...oidcConfig}
      // Clean up auth callback query params from the URL bar after login.
      // After Keycloak redirects back with ?code=...&state=..., the OIDC
      // library processes them. This callback fires post-exchange to strip
      // the code/state params so the URL bar shows a clean path.
      onSigninCallback={() => {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }}
    >
      <AuthBridge>{children}</AuthBridge>
    </OidcAuthProvider>
  );
}

/**
 * Hook to access auth state from any component.
 *
 * Usage:
 *   const { user, isAuthenticated, login, logout, token } = useAuth();
 */
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
